import * as FileSystem from 'expo-file-system/legacy';
import { Platform } from 'react-native';

import type { ApolloCache } from '@apollo/client';
import type { LocalFile } from '@graphql/generated';

/**
 * TA-10885 — recovering from a BLOB_NOT_FOUND on send.
 *
 * The upload manager dedupes by file content hash against a blobId map that is
 * persisted across launches (hashUploadMap in useChatUploadManager). A hit
 * skips the upload entirely and reuses the stored blobId, so an attachment can
 * be marked "uploaded" against a blob the server no longer holds. Offline that
 * map is the ONLY way a blobId can appear at all — no upload can run — which is
 * why the offline repro lands on it every time.
 *
 * The send then fails with BLOB_NOT_FOUND and the message is retired as
 * "The file has been deleted and cannot be sent", even though the file is still
 * sitting on the device. The fix is to check before believing the server: if
 * the local file is still there, the blobId is stale, not the file.
 */
export type BlobNotFoundRecovery =
  | { action: 'reupload'; files: LocalFile[] }
  | { action: 'retire' };

export const planBlobNotFoundRecovery = ({
  files,
  existingClientIds,
  alreadyRetried,
}: {
  /** The files whose blobIds were sent with the failed mutation. */
  files: LocalFile[];
  /** clientIds of those files that still exist on disk. */
  existingClientIds: Set<string>;
  /** Whether this message has already been through one re-upload. */
  alreadyRetried: boolean;
}): BlobNotFoundRecovery => {
  // One retry only. A second BLOB_NOT_FOUND came back against a blobId this
  // device just minted from the real bytes, so the blob really is gone on the
  // server side and re-uploading again would loop forever.
  if (alreadyRetried) return { action: 'retire' };

  if (files.length === 0) return { action: 'retire' };

  // All or nothing: a message with one vanished attachment can never send with
  // that attachment, so re-uploading the survivors only buys another failure.
  const allPresent = files.every((file) =>
    existingClientIds.has(file.clientId)
  );

  return allPresent ? { action: 'reupload', files } : { action: 'retire' };
};

// Message clientIds that have already spent their one re-upload. Module-scoped
// for two reasons (Copilot review): a component ref lost the budget whenever
// the chat screen unmounted, so leaving and reopening the chat handed the same
// message another retry; and the two send paths (ChatMessageData and
// useChatProcessing, which can also be mounted twice) must share one budget
// rather than each spending their own.
//
// Not persisted across app restarts on purpose. The retry re-uploads the real
// bytes, so the only way to see a second BLOB_NOT_FOUND is a blob the server
// dropped between that upload and the send — and a fresh launch re-uploading
// once more is the right behaviour there, not a loop.
const blobRecoveryRetriedClientIds = new Set<string>();

export const hasRetriedBlobRecovery = (clientId: string): boolean =>
  blobRecoveryRetriedClientIds.has(clientId);

export const markBlobRecoveryRetried = (clientId: string): void => {
  blobRecoveryRetriedClientIds.add(clientId);
};

/** Cleared on user switch, alongside the upload dedup map it is paired with. */
export const clearBlobRecoveryRetries = (): void => {
  blobRecoveryRetriedClientIds.clear();
};

/**
 * clientIds of the files still present on the device. Web has no local file to
 * re-read (the url is a revocable blob: handle), so nothing there is
 * recoverable and every caller falls through to the existing retire path.
 */
export const listExistingLocalFileClientIds = async (
  files: LocalFile[]
): Promise<Set<string>> => {
  if (Platform.OS === 'web') return new Set<string>();

  const present = await Promise.all(
    files.map(async (file) => {
      const uri = file.url || file.path;
      if (!uri) return null;
      try {
        const info = await FileSystem.getInfoAsync(uri);
        return info.exists ? file.clientId : null;
      } catch {
        return null;
      }
    })
  );

  return new Set(present.filter((clientId): clientId is string => !!clientId));
};

/**
 * Drop the blobId the cache holds for each document so the upload pipeline
 * stops treating it as already uploaded. Document is keyed on clientId
 * (initializeCache), and writing the field re-broadcasts listDraftDocuments,
 * which is what re-runs the uploader.
 */
export const clearCachedBlobIds = (
  cache: ApolloCache<unknown>,
  files: LocalFile[]
) => {
  files.forEach((file) => {
    const id = cache.identify({
      __typename: 'Document',
      clientId: file.clientId,
    });
    if (!id) return;
    cache.modify({ id, fields: { blobId: () => '' } });
  });
};
